<div class="pub-scroll">
    <div class="pub-page">
        <header class="pub-brand">
            <div class="pub-brand-mark">
                <img 
                    src="assets/images/logos/FL_Health_2026.png" 
                    alt="Florida Department of Health" 
                    class="pub-logo" 
                />
                <div class="pub-brand-titles">
                    <div class="pub-brand-bureau">Bureau of Public Health Pharmacy</div>
                    <div class="pub-brand-program">Medication Assistance Program &middot; Insulin Distribution Program</div>
                </div>
            </div>
            <div class="pub-brand-help">Need help? Call <strong>850-922-9036</strong></div>
        </header>

        <div class="pub-intro">
            <h1>Insulin Distribution Program Application</h1>
            <p>
                Complete this application to request insulin or diabetes management supplies through your
                County Health Department. Your answers are reviewed by CHD staff, who will contact you about
                next steps. A prescription from your provider is required before medication can be dispensed.
            </p>
        </div>

        @if (submitted()) {
            <section class="pub-card pub-confirmation">
                <div 
                    class="pub-tick" 
                    aria-hidden="true"
                >&#10003;</div>
                <!-- Focused on arrival. A live region inserted at the same moment it should announce is
                    read inconsistently, whereas moving focus to the heading is reliable and also puts a
                    keyboard user at the top of the new content. -->
                <h2 
                    class="pub-confirmation-heading" 
                    tabindex="-1"
                >Application submitted</h2>
                <p>
                    Your application has been sent to
                    <strong>{{ facilityName() || 'your County Health Department' }}</strong>
                    for review. Keep your confirmation number for your records.
                </p>

                <div class="pub-confirmation-number">
                    <span class="pub-confirmation-label">Confirmation</span>
                    <span class="pub-confirmation-value">{{ confirmationNumber() }}</span>
                </div>

                <div class="pub-next">
                    <div class="pub-next-title">What happens next</div>
                    <ol class="pub-next-list">
                        <li>CHD staff review your application and determine eligibility.</li>
                        <li>If approved, your facility contacts you to schedule an appointment with a provider.</li>
                    </ol>
                </div>

                <p-button 
                    label="Start another application" 
                    severity="secondary" 
                    [outlined]="true"
                    (click)="startAnother()" 
                />
            </section>
        } @else {
            <form 
                class="pub-layout" 
                [formGroup]="form" 
                (ngSubmit)="submit()" 
                novalidate
            >
                <nav 
                    class="pub-steps" 
                    aria-label="Application sections"
                >
                    <div class="pub-steps-title">Application sections</div>
                    @for (s of steps; track s.key) {
                        <button 
                            type="button" 
                            class="pub-step"
                            [class.active]="currentStep() === s.key"
                            [class.locked]="!canNavigateTo(s.key)"
                            [disabled]="!canNavigateTo(s.key)"
                            [attr.aria-current]="currentStep() === s.key ? 'step' : null"
                            (click)="goToStep(s.key)"
                        >
                            <i 
                                class="pi pub-step-mark"
                                [class.pi-check-circle]="isStepComplete(s.key)"
                                [class.done]="isStepComplete(s.key)"
                                [class.pi-lock]="!isStepComplete(s.key) && !canNavigateTo(s.key)"
                                [class.pi-circle]="!isStepComplete(s.key) && canNavigateTo(s.key)"
                                aria-hidden="true"
                            ></i>
                            <span>{{ s.label }}</span>
                        </button>
                    }
                </nav>

                <div class="pub-content">
                    <h2 
                        class="pub-step-heading" 
                        tabindex="-1"
                    >
                        Step {{ currentIndex() + 1 }} of {{ steps.length }}
                    </h2>

                    <div formGroupName="data">
                        @switch (currentStep()) {
                            @case ('applicant') {
                                <pcg-applicant-info-section
                                    [group]="applicant"
                                    [isInsulin]="true"
                                    [showClientId]="false"
                                    [countyApi]="countyApi"
                                    [showAllStates]="showAllStates"
                                    [showDeliveryType]="false"
                                />
                            }
                            @case ('medical') { <pcg-medical-info-section [group]="medical" /> }
                            @case ('conditions') {
                                <pcg-health-conditions-section
                                    [group]="conditions"
                                    (conditionToggled)="maForm.toggleCondition($event)"
                                />
                            }
                            @case ('acknowledgment') {
                                <!-- Which CHD reviews the application. Every active one is offered: an
                                    applicant may deal with a CHD outside the county they live in. Each is
                                    listed with its address, and the filter matches the address too, so an
                                    applicant can find the office nearest them by city. -->
                                <div 
                                    class="gf-field pub-servicing" 
                                    [formGroup]="form"
                                    [class.has-error]="form.get('facilityId').invalid && form.get('facilityId').touched"
                                >
                                    <label for="servicingChd">County Health Department that will review your application</label>
                                    <p-select 
                                        inputId="servicingChd" 
                                        [options]="servicingFacilities()"
                                        optionLabel="facilityName" 
                                        optionValue="facilityId"
                                        formControlName="facilityId" 
                                        [filter]="true"
                                        filterBy="facilityName,address,city,zip"
                                        placeholder="Select a County Health Department" 
                                        fluid
                                    >
                                        <ng-template 
                                            #selectedItem 
                                            let-facility
                                        >
                                            <span class="pub-facility-option">
                                                <span class="pub-facility-name">{{ facility.facilityName }}</span>
                                                <span class="pub-facility-address">{{ facilityAddress(facility) }}</span>
                                            </span>
                                        </ng-template>

                                        <ng-template 
                                            #item 
                                            let-facility
                                        >
                                            <span class="pub-facility-option">
                                                <span class="pub-facility-name">{{ facility.facilityName }}</span>
                                                <span class="pub-facility-address">{{ facilityAddress(facility) }}</span>
                                            </span>
                                        </ng-template>
                                    </p-select>
                                    <small 
                                        class="gf-error"
                                        [class.gf-error--hidden]="!(form.get('facilityId').invalid && form.get('facilityId').touched)"
                                    >Required</small>
                                </div>

                                <pcg-acknowledgment-section
                                    [group]="acknowledgment"
                                    [showMailTo]="false"
                                    [signature]="signature()"
                                    (signatureChanged)="onSignatureChange($event)"
                                    [showGuardian]="isMinor"
                                />
                            }
                        }
                    </div>

                    <!-- Positioned off-screen rather than hidden from assistive technology: an
                        aria-hidden container holding a focusable input is itself an accessibility
                        defect, and a screen-reader user who reaches this is told plainly to skip it.
                        What catches a bot is that the field is invisible to a sighted user, not that it
                        is concealed from everyone. -->
                    <div class="pub-honeypot">
                        <label for="hp">Leave this field blank</label>
                        <input 
                            id="hp" 
                            type="text" 
                            formControlName="honeyPot" 
                            tabindex="-1"
                            autocomplete="off" 
                        />
                    </div>

                    @if (
                        isLastStep() 
                        && submitAttempted() 
                        && incompleteSteps().length
                    ) {
                        <div 
                            class="pub-summary" 
                            role="alert"
                        >
                            <div class="pub-summary-title">Before you can submit, please finish:</div>
                            <ul>
                                @for (s of incompleteSteps(); track s.key) {
                                    <li>
                                        <button 
                                            type="button" 
                                            class="pub-summary-link" 
                                            (click)="goToStep(s.key)"
                                        >{{ s.label }}</button>
                                    </li>
                                }
                            </ul>
                        </div>
                    }

                    <div class="pub-actions">
                        @if (!isFirstStep()) {
                            <p-button 
                                type="button" 
                                label="Back" 
                                severity="secondary" 
                                [outlined]="true"
                                icon="pi pi-chevron-left" 
                                (click)="previous()" 
                            />
                        }
                        <span class="pub-actions-spacer"></span>
                        @if (!isLastStep()) {
                            <p-button 
                                type="button" 
                                label="Continue" 
                                icon="pi pi-chevron-right"
                                iconPos="right" 
                                (click)="next()" 
                            />
                        } @else {
                            <p-button 
                                type="submit" 
                                label="Submit Application" 
                                icon="pi pi-check"
                                [disabled]="isSubmitting()" 
                                [loading]="isSubmitting()" 
                            />
                        }
                    </div>

                    <p class="pub-privacy">
                        This page is protected by reCAPTCHA. Your answers are transmitted securely and are
                        visible only to the County Health Department reviewing your application.
                    </p>
                </div>
            </form>
        }
    </div>
</div>
